import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Layers, ImageIcon, Film, Sparkles, Loader2, Pencil, Plus, Trash2, ChevronUp, ChevronDown, Maximize2 } from 'lucide-react';
import Cropper from 'react-cropper';
import "cropperjs/dist/cropper.css";
import api from '../api';
import ImagePickerModal from './ImagePickerModal';
import CoverCropperModal from './CoverCropperModal';
import FullscreenTextEditor from './FullscreenTextEditor';
// 🆕 工作流长图卡片尺寸（270x86，与 AssetDrawer.js 抽屉里的展示比例保持一致）
// 封面裁剪比例必须跟展示比例一致，否则会被卡片渲染时的 objectFit:cover 二次裁切
import { WORKFLOW_CARD_WIDTH, WORKFLOW_CARD_HEIGHT } from './AssetDrawer';

// 预设分类定义
// 注：text 类型的下拉会在运行时合并已有的全局自定义分类（见 SaveAssetModal）
const CATEGORIES = {
  image: [
    { id: 'role', label: '角色库 (Roles)' },
    { id: 'scene', label: '场景库 (Scenes)' },
    { id: 'prop', label: '道具库 (Props)' }
  ],
  video: [
    { id: 'role', label: '角色库 (Roles)' },
    { id: 'motion', label: '动作库 (Motion)' },
    { id: 'vfx', label: '特效库 (VFX)' }
  ],
  audio: [
    { id: 'music', label: '音乐库 (Music)' },
    { id: 'sfx', label: '音效库 (SFX)' },
    { id: 'voice', label: '音色库 (Voice)' }
  ],
  text: [
    { id: 'prompt', label: '咒语库 (Prompts)' }
  ],
  workflow: [
    { id: 'default', label: '默认分类' }
  ]
};

// 文本节点保存时的初始默认分类 id（仅作为"新打开模态框时下拉的默认选中项"）
export const DEFAULT_SPELL_CATEGORY_ID = 'prompt';

const modalStyle = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
    // 全屏模态须盖住画布左侧工具条(zIndex 10000)和右键菜单(100000)。原 4000 太低，
    // 工具条会穿透显示在对话框上。统一到项目其它全屏 modal 的 999999。
    zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  container: {
    width: '700px', height: '450px', // 加宽加高以容纳裁剪预览
    background: 'var(--bg-panel)', border: '1px solid var(--border-subtle)',
    borderRadius: '16px', boxShadow: '0 25px 60px rgba(0,0,0,0.9)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column'
  },
  header: {
    padding: '16px 24px', borderBottom: '1px solid var(--border-subtle)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--save-header-bg)'
  },
  body: { flex: 1, display: 'flex', overflow: 'hidden' }, // 分左右栏
  leftCol: { width: '300px', background: 'var(--save-preview-bg)', position: 'relative', borderRight: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  rightCol: { flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' },
  
  footer: {
    padding: '16px 24px', borderTop: '1px solid var(--border-subtle)',
    display: 'flex', justifyContent: 'flex-end', gap: '12px', background: 'var(--bg-panel)'
  },
  label: { display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 'bold' },
  input: {
    width: '100%', padding: '12px', background: '#09090b', border: '1px solid #333',
    borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', transition: 'border 0.2s',
    boxSizing: 'border-box' // 强制包含 padding
  },

};

// 🆕 工作流专属版式：裁剪的是横版长图（270:86），左右分栏给裁剪器的操作空间太小。
// 改成上下结构——裁剪器占满整个上半区（更宽更高，裁剪更精确），标题/分类等表单挪到下半区。
// 只影响 nodeData.type === 'workflow'，不改动 image/video/audio/text 原有的左右分栏版式。
const workflowModalStyle = {
  container: {
    width: '820px', height: '620px',
    background: 'var(--bg-panel)', border: '1px solid var(--border-subtle)',
    borderRadius: '16px', boxShadow: '0 25px 60px rgba(0,0,0,0.9)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column'
  },
  body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }, // 上下分区
  topArea: {
    flex: 1, minHeight: 0, background: 'var(--save-preview-bg)', position: 'relative',
    borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  bottomArea: {
    flexShrink: 0, padding: '18px 24px', display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'flex-end'
  },
};

// 工具：生成视频的一帧并中心裁剪为正方形 Blob
const generateVideoThumbnail = (videoUrl) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = "anonymous";
    video.muted = true;
    
    // 加载元数据后跳转到中间
    video.onloadedmetadata = () => {
      video.currentTime = video.duration > 1 ? 1 : 0; // 取第1秒或开头
    };

    video.onseeked = () => {
      const size = Math.min(video.videoWidth, video.videoHeight);
      const canvas = document.createElement('canvas');
      canvas.width = 200; // 统一定义缩略图尺寸
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      
      // 计算中心裁剪区域
      const sx = (video.videoWidth - size) / 2;
      const sy = (video.videoHeight - size) / 2;
      
      // 绘制：源(sx, sy, size, size) -> 目标(0, 0, 200, 200)
      ctx.drawImage(video, sx, sy, size, size, 0, 0, 200, 200);
      
      canvas.toBlob((blob) => {
        resolve(blob);
        // 清理
        video.remove();
      }, 'image/jpeg', 0.85);
    };
    
    video.onerror = () => resolve(null);
  });
};

// --- 自定义 Select 组件 ---
// 支持每行附带管理操作（上移/下移/重命名/删除）以及末尾"新建项"按钮
const CustomSelect = ({
  options,
  value,
  onChange,
  onRenameOption,    // (option) => void
  onDeleteOption,    // (option) => void
  onMoveOption,      // (option, direction:-1|1) => void
  onAddOption,       // () => void
  manageable = false, // 显示行内管理图标（重命名/删除/上下移动）
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null); // {left, top, width} 用于 fixed 定位
  const containerRef = useRef(null);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 打开时计算 trigger 位置（fixed 渲染避免被父级 overflow:hidden 裁剪）
  useEffect(() => {
    if (!isOpen) return;
    const updateRect = () => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      setMenuRect({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  // 行内小图标按钮
  const IconBtn = ({ icon: Icon, title, onClick, color = '#94a3b8' }) => (
    <span
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '22px', height: '22px', borderRadius: '4px',
        color, flexShrink: 0,
        transition: 'all 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = color; }}
    >
      <Icon size={12} />
    </span>
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        background: '#09090b',
        border: isOpen ? '1px solid #3b82f6' : '1px solid #333',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#fff',
        transition: 'border-color 0.2s'
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      {/* Trigger 区域 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px' }}>
         <span>{selectedOption?.label || "请选择"}</span>
         <div style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'flex', color: '#666' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
         </div>
      </div>

      {/* Dropdown 列表 — position:fixed 避免被 modal body 的 overflow:hidden 裁剪 */}
      {isOpen && menuRect && (
        <div style={{
           position: 'fixed',
           left: menuRect.left,
           top: menuRect.top,
           width: menuRect.width,
           background: '#18181b',
           border: '1px solid #333',
           borderRadius: '8px',
           zIndex: 5000,
           boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
           overflow: 'hidden',
           maxHeight: '280px',
           overflowY: 'auto'
        }}>
            {options.map((opt, idx) => {
              const showManage = manageable && !opt.locked;
              const isFirst = idx === 0;
              const isLast = idx === options.length - 1;
              return (
                <div
                  key={opt.value}
                  onClick={(e) => {
                      e.stopPropagation();
                      onChange(opt.value);
                      setIsOpen(false);
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#27272a'}
                  onMouseLeave={e => e.currentTarget.style.background = opt.value === value ? '#27272a' : 'transparent'}
                  style={{
                      padding: '8px 10px',
                      background: opt.value === value ? '#27272a' : 'transparent',
                      color: opt.value === value ? '#3b82f6' : '#cbd5e1',
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                  }}
                >
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                    {showManage && onMoveOption && (
                      <>
                        <IconBtn
                          icon={ChevronUp}
                          title="上移"
                          onClick={() => !isFirst && onMoveOption(opt, -1)}
                          color={isFirst ? '#444' : '#94a3b8'}
                        />
                        <IconBtn
                          icon={ChevronDown}
                          title="下移"
                          onClick={() => !isLast && onMoveOption(opt, 1)}
                          color={isLast ? '#444' : '#94a3b8'}
                        />
                      </>
                    )}
                    {showManage && onRenameOption && (
                      <IconBtn icon={Pencil} title="重命名" onClick={() => { setIsOpen(false); onRenameOption(opt); }} />
                    )}
                    {showManage && onDeleteOption && (
                      <IconBtn icon={Trash2} title="删除" color="#fca5a5" onClick={() => { setIsOpen(false); onDeleteOption(opt); }} />
                    )}
                </div>
              );
            })}

            {onAddOption && (
              <div
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); onAddOption(); }}
                onMouseEnter={e => e.currentTarget.style.background = '#27272a'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                style={{
                  padding: '10px 12px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#3b82f6',
                  fontWeight: 500,
                }}
              >
                <Plus size={14} />
                <span>新建分类</span>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default function SaveAssetModal({
  isOpen,
  nodeData,
  onClose,
  onSave,
  isSuperAdmin,
  projectId, // 🆕 用于加载当前画板的图片
}) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState(''); // 🆕 描述词：整段进提示词传给模型
  const [showDescFullscreen, setShowDescFullscreen] = useState(false); // 🆕 描述词全屏编辑
  const [videoThumbUrl, setVideoThumbUrl] = useState(null);
  const [videoThumbBlob, setVideoThumbBlob] = useState(null);
  const [replacedImageFile, setReplacedImageFile] = useState(null); // 🆕 编辑资产时替换的新图片文件
  const [currentImageSrc, setCurrentImageSrc] = useState(null); // 当前显示的图片源（替换图片后更新）
  const [saving, setSaving] = useState(false); // 🛡️ 防止重复点击
  const [isGlobalSpell, setIsGlobalSpell] = useState(false); // 🌍 是否部署为全局咒语
  const [isEncryptedSpell, setIsEncryptedSpell] = useState(false); // 🔒 是否加密
  // 🆕 动态加载的分类列表
  const [workflowCategories, setWorkflowCategories] = useState([]);
  const [imageCategories, setImageCategories] = useState([]);
  const [videoCategories, setVideoCategories] = useState([]);
  const [audioCategories, setAudioCategories] = useState([]);
  const [textCategories, setTextCategories] = useState([]);
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null); // 🆕 文件选择器引用（编辑资产时替换图片）
  const videoThumbUrlRef = useRef(null); // 🛡️ 追踪最新 blob URL，避免闭包陈旧导致泄漏

  // 🆕 工作流封面设置
  const [showWorkflowCoverPicker, setShowWorkflowCoverPicker] = useState(false);
  const [workflowImages, setWorkflowImages] = useState([]);
  const [workflowCoverCropUrl, setWorkflowCoverCropUrl] = useState(null);
  const [workflowCoverBlob, setWorkflowCoverBlob] = useState(null);
  const workflowCropperRef = useRef(null); // 🆕 工作流裁剪器引用（仅 single 模式使用）

  // 🆕 工作流封面模式：'single'（一次裁剪整张长图，原逻辑）｜ 'split'（左右两次裁剪拼接）
  const [coverMode, setCoverMode] = useState('single');
  // split 模式：左右两边各自的裁剪源图 URL + 已裁剪出的半图 blob
  const [splitLeftUrl, setSplitLeftUrl] = useState(null);
  const [splitRightUrl, setSplitRightUrl] = useState(null);
  const [splitLeftBlob, setSplitLeftBlob] = useState(null);
  const [splitRightBlob, setSplitRightBlob] = useState(null);
  const [pickerTargetSide, setPickerTargetSide] = useState(null); // 打开 ImagePickerModal 时记录选图是给哪一半用
  // 🛡️ split 模式左右两个裁剪器各自独立的 ref——两边同时挂载、同时可操作，不再共用一个 ref 互相顶掉
  const splitLeftCropperRef = useRef(null);
  const splitRightCropperRef = useRef(null);

  // 🆕 动态加载分类列表
  useEffect(() => {
    if (!isOpen) return;

    const loadCategories = async () => {
      try {
        // 不再限制 scope，加载所有层级的分类（personal/project/global）
        const data = await api.get('/api/asset-categories');
        const categories = data.categories || [];

        console.log('📦 [SaveAssetModal] 加载到的分类:', categories);

        // 🔧 按 category_key 去重（三个库 personal/project/global 只显示一个）
        const uniqueMap = new Map();
        categories.forEach(cat => {
          const key = cat.category_key;
          if (!uniqueMap.has(key)) {
            uniqueMap.set(key, cat);
          }
        });
        const uniqueCategories = Array.from(uniqueMap.values());
        console.log('✨ 去重后的分类:', uniqueCategories);

        // 工作流类型分类
        // 🛡️ 带上 scope：保存时要按分类的 scope 判断该不该强制 is_global，
        // 否则「分类标签是 global，但资产归属字段没跟着变成全局」会产生孤儿资产
        // （资料库任何视图都查不到，但删分类时的计数又能扫到，删不掉）
        const workflowCats = uniqueCategories
          .filter(cat => cat.type === 'workflow')
          .map(cat => ({ id: cat.category_key, label: cat.name, scope: cat.scope }));
        console.log('🔧 工作流分类:', workflowCats);
        setWorkflowCategories(workflowCats);

        // 资产库分类（type='library'），按 asset_types 字段过滤
        const libraryCats = uniqueCategories.filter(cat => cat.type === 'library');
        console.log('📚 资产库分类:', libraryCats);

        // image 分类：asset_types 包含 'image' 或 icon 是 'photo'
        const imageCats = libraryCats
          .filter(cat =>
            (cat.asset_types && cat.asset_types.includes('image')) ||
            cat.icon === 'photo'
          )
          .map(cat => ({ id: cat.category_key, label: cat.name, scope: cat.scope }));
        console.log('🖼️ 图片分类:', imageCats);
        setImageCategories(imageCats);

        // video 分类：asset_types 包含 'video' 或 icon 是 'film'
        const videoCats = libraryCats
          .filter(cat =>
            (cat.asset_types && cat.asset_types.includes('video')) ||
            cat.icon === 'film'
          )
          .map(cat => ({ id: cat.category_key, label: cat.name, scope: cat.scope }));
        console.log('🎬 视频分类:', videoCats);
        setVideoCategories(videoCats);

        // audio 分类：asset_types 包含 'audio' 或 icon 是 'music'
        const audioCats = libraryCats
          .filter(cat =>
            (cat.asset_types && cat.asset_types.includes('audio')) ||
            cat.icon === 'music'
          )
          .map(cat => ({ id: cat.category_key, label: cat.name, scope: cat.scope }));
        console.log('🎵 音频分类:', audioCats);
        setAudioCategories(audioCats);

        // 🆕 text 分类：asset_types 包含 'text' 或 icon 是 'text'
        const textCats = libraryCats
          .filter(cat =>
            (cat.asset_types && cat.asset_types.includes('text')) ||
            cat.icon === 'text'
          )
          .map(cat => ({ id: cat.category_key, label: cat.name, scope: cat.scope }));
        console.log('📝 文本分类:', textCats);
        setTextCategories(textCats);

      } catch (error) {
        console.error('❌ 加载分类失败:', error);
        // 失败时用空数组，不影响其他功能
        setWorkflowCategories([]);
        setImageCategories([]);
        setVideoCategories([]);
        setAudioCategories([]);
        setTextCategories([]);
      }
    };

    loadCategories();
  }, [isOpen]);

  // 🆕 当分类加载完成后，设置默认分类
  useEffect(() => {
    if (!isOpen || !nodeData || category) return; // 已有分类则不覆盖

    const type = nodeData.type;
    let defaultCat = '';

    if (type === 'workflow' && workflowCategories.length > 0) {
      defaultCat = workflowCategories[0].id;
    } else if (type === 'image' && imageCategories.length > 0) {
      defaultCat = imageCategories[0].id;
    } else if (type === 'video' && videoCategories.length > 0) {
      defaultCat = videoCategories[0].id;
    } else if (type === 'audio' && audioCategories.length > 0) {
      defaultCat = audioCategories[0].id;
    } else if (type === 'text' && textCategories.length > 0) {
      defaultCat = textCategories[0].id;
    }

    if (defaultCat) {
      setCategory(defaultCat);
    }
  }, [workflowCategories, imageCategories, videoCategories, audioCategories, textCategories, isOpen, nodeData, category]);

  // 初始化

  useEffect(() => {
    let cancelled = false; // 🛡️ 防止组件关闭后仍调用 setState
    if (isOpen && nodeData) {
      setSaving(false); // 🛡️ 重新打开时重置保存状态
      setIsGlobalSpell(false); // 重新打开时默认不部署全局
      setIsEncryptedSpell(false); // 重新打开时默认不加密
      setTitle(nodeData.title || '未命名资产');
      setDescription(nodeData.description || ''); // 🆕 已有描述词则回填

      // 🆕 重置编辑模式状态 + 当前图片源
      setReplacedImageFile(null);
      setCurrentImageSrc(nodeData.localCacheUrl || nodeData.output || nodeData.url);

      // 所有类型都等待动态分类加载后设置
      setCategory(''); // 先清空，等动态分类加载完

      // 🆕 重置工作流封面状态
      setShowWorkflowCoverPicker(false);
      setWorkflowImages([]);
      setWorkflowCoverCropUrl(null);
      setWorkflowCoverBlob(null);
      // 🆕 重置左右拼接模式状态（重新打开时默认回到单图模式）
      setCoverMode('single');
      setSplitLeftUrl(null);
      setSplitRightUrl(null);
      setSplitLeftBlob(null);
      setSplitRightBlob(null);
      setPickerTargetSide(null);

      // 如果是视频，自动生成缩略图（优先用本地缓存 blob URL，避免跨域问题）
      const videoSrc = nodeData.localCacheUrl || nodeData.output;
      if (nodeData.type === 'video' && videoSrc) {
        generateVideoThumbnail(videoSrc).then(blob => {
            if (blob && !cancelled) {
                setVideoThumbBlob(blob);
                const url = URL.createObjectURL(blob);
                videoThumbUrlRef.current = url;
                setVideoThumbUrl(url);
            }
        });
      }
    }
    return () => {
        cancelled = true;
        if (videoThumbUrlRef.current) {
            URL.revokeObjectURL(videoThumbUrlRef.current);
            videoThumbUrlRef.current = null;
        }
    }
  }, [isOpen, nodeData]);

  // 【修复】监听分类变化，强制更新裁剪框比例
  useEffect(() => {
    if (cropperRef.current && cropperRef.current.cropper) {
      const isRole = category === 'role';
      cropperRef.current.cropper.setAspectRatio(isRole ? 86 / 142 : 1);
    }
  }, [category]);

  // 🆕 处理工作流封面选择
  // side: null = single 模式（选整图裁剪）｜ 'left' | 'right' = split 模式（选某一半的源图）
  const handleSetWorkflowCover = async (side = null) => {
    if (!projectId) {
      console.warn('无法获取当前图板 ID');
      return;
    }
    try {
      const data = await api.get(`/api/projects/${projectId}`);
      const nodes = data.nodes || [];
      const images = nodes
        .filter(n => n.data?.type === 'image' && (n.data?.localCacheUrl || n.data?.output))
        .map(n => ({
          url: n.data.localCacheUrl || n.data.output,
          title: n.data.title || '未命名'
        }));

      if (images.length === 0) {
        alert('当前图板中没有图片资产');
        return;
      }

      setWorkflowImages(images);
      setPickerTargetSide(side);
      setShowWorkflowCoverPicker(true);
    } catch (err) {
      console.error('加载图片失败:', err);
    }
  };

  const handleSelectWorkflowCoverImage = (url) => {
    setShowWorkflowCoverPicker(false);
    // 🛡️ split 模式左右两侧各自独立挂载 <Cropper>，换源图只影响被点击的那一侧，
    // 不会像之前共用一个 ref 那样把另一侧顶掉/卸载
    if (pickerTargetSide === 'left') {
      setSplitLeftUrl(url);
      setSplitLeftBlob(null); // 换源图，之前裁的半图作废
    } else if (pickerTargetSide === 'right') {
      setSplitRightUrl(url);
      setSplitRightBlob(null);
    } else {
      setWorkflowCoverCropUrl(url);
    }
    setPickerTargetSide(null);
  };

  // 🆕 从裁剪器按给定尺寸导出一个 blob（Promise 化 cropper.getCroppedCanvas + toBlob）
  const exportCropperBlob = (cropper, width, height) => {
    return new Promise((resolve) => {
      if (!cropper) { resolve(null); return; }
      cropper.getCroppedCanvas({
        width, height,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      }).toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
  };

  // 🆕 把左右两张半图（各 135x86）无缝拼接成一张完整长图（270x86）
  const compositeSideBySide = (leftBlob, rightBlob) => {
    return new Promise((resolve, reject) => {
      const halfW = Math.round(512 / 2);
      const outH = Math.round(512 * (WORKFLOW_CARD_HEIGHT / WORKFLOW_CARD_WIDTH));
      const canvas = document.createElement('canvas');
      canvas.width = halfW * 2;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');

      const loadImg = (blob) => new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = URL.createObjectURL(blob);
      });

      Promise.all([loadImg(leftBlob), loadImg(rightBlob)])
        .then(([leftImg, rightImg]) => {
          ctx.drawImage(leftImg, 0, 0, halfW, outH);
          ctx.drawImage(rightImg, halfW, 0, halfW, outH);
          URL.revokeObjectURL(leftImg.src);
          URL.revokeObjectURL(rightImg.src);
          canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
        })
        .catch(reject);
    });
  };

  // 🆕 在保存时从裁剪器获取最终封面 blob（single 模式：一次裁剪整图；split 模式：左右各裁一半再拼接）
  const getWorkflowCoverBlobFromCropper = async () => {
    if (isWorkflowType && coverMode === 'split') {
      const halfW = Math.round(512 / 2);
      const halfH = Math.round(512 * (WORKFLOW_CARD_HEIGHT / WORKFLOW_CARD_WIDTH));

      // 🛡️ 左右两侧各自独立挂载，两个裁剪器实例此刻都还在，直接各自导出即可，
      // 不需要再靠"当前正在编辑哪一侧"这种状态去猜——这正是之前互相顶掉的根源
      const leftCropper = splitLeftCropperRef.current?.cropper;
      const rightCropper = splitRightCropperRef.current?.cropper;
      const [leftBlob, rightBlob] = await Promise.all([
        leftCropper ? exportCropperBlob(leftCropper, halfW, halfH) : Promise.resolve(splitLeftBlob),
        rightCropper ? exportCropperBlob(rightCropper, halfW, halfH) : Promise.resolve(splitRightBlob),
      ]);

      if (!leftBlob || !rightBlob) {
        return { __error: '请先完成左右两张图的裁剪' };
      }

      return compositeSideBySide(leftBlob, rightBlob);
    }

    // single 模式（工作流整图 或 咒语/文本正方形封面）
    if (!workflowCoverCropUrl) {
      // 没有在裁剪，直接用已有的 blob
      return workflowCoverBlob;
    }

    // 正在裁剪，从裁剪器获取
    const cropper = workflowCropperRef.current?.cropper;
    if (!cropper) return null;

    // 🆕 工作流封面按长图比例（270:86）导出，避免展示时被 objectFit:cover 二次裁切；咒语保持正方形 512x512
    const outWidth = 512;
    const outHeight = isWorkflowType ? Math.round(512 * (WORKFLOW_CARD_HEIGHT / WORKFLOW_CARD_WIDTH)) : 512;

    return exportCropperBlob(cropper, outWidth, outHeight);
  };

  // 🆕 处理替换图片（编辑资产模式）
  const handleReplaceImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 🆕 处理文件选择（编辑资产模式替换图片）
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const newSrc = event.target.result;
      setCurrentImageSrc(newSrc);
      setReplacedImageFile(file);

      // 裁剪器需要重新载入新图
      if (cropperRef.current?.cropper) {
        cropperRef.current.cropper.replace(newSrc);
      }
    };
    reader.readAsDataURL(file);

    // 清空 input 值，允许重复选择同一文件
    e.target.value = '';
  };

  if (!isOpen || !nodeData) return null;

  // 🆕 判断是否为编辑资产模式
  const isEditingAsset = !!nodeData.isEditingAsset;
  const editingAssetId = nodeData.editingAssetId;

  const handleSave = async () => {
    if (saving) return; // 🛡️ 防止重复点击
    setSaving(true);

    // 🆕 编辑资产模式：更新已有资产（可选择替换图片）
    if (isEditingAsset && editingAssetId) {
      let finalThumbnailBlob = null;
      let finalMiniThumbnailBlob = null;
      const isRole = category === 'role';
      const aspectWidth = 200;
      const aspectHeight = isRole ? Math.round(200 * (142 / 86)) : 200;

      const callOnSave = async (payload) => {
        try {
          await onSave(payload);
        } finally {
          setSaving(false);
        }
      };

      if (replacedImageFile) {
        // 替换了图片：重新生成缩略图
        if (nodeData.type === 'image' && cropperRef.current) {
          const canvas = cropperRef.current.cropper.getCroppedCanvas({
            width: aspectWidth, height: aspectHeight,
            imageSmoothingQuality: 'high'
          });
          if (canvas) {
            await new Promise((resolve) => {
              canvas.toBlob((blob) => {
                finalThumbnailBlob = blob;
                const miniCanvas = cropperRef.current.cropper.getCroppedCanvas({
                  width: 80, height: 80,
                  imageSmoothingQuality: 'high'
                });
                miniCanvas.toBlob((miniBlob) => {
                  finalMiniThumbnailBlob = miniBlob;
                  resolve();
                }, 'image/jpeg', 0.80);
              }, 'image/jpeg', 0.85);
            });
          }
        } else if (nodeData.type === 'video') {
          finalThumbnailBlob = videoThumbBlob;
          if (videoThumbBlob) {
            const img = new Image();
            const objectUrl = URL.createObjectURL(videoThumbBlob);
            await new Promise((resolve) => {
              img.onload = () => {
                const miniCanvas = document.createElement('canvas');
                miniCanvas.width = 80;
                miniCanvas.height = 80;
                const ctx = miniCanvas.getContext('2d');
                ctx.drawImage(img, 0, 0, 80, 80);
                miniCanvas.toBlob((miniBlob) => {
                  URL.revokeObjectURL(objectUrl);
                  finalMiniThumbnailBlob = miniBlob;
                  resolve();
                }, 'image/jpeg', 0.80);
              };
              img.src = objectUrl;
            });
          }
        }

        callOnSave({
          assetId: editingAssetId,
          title,
          description,
          thumbnailBlob: finalThumbnailBlob,
          miniThumbnailBlob: finalMiniThumbnailBlob,
          newImageFile: replacedImageFile,
          isEditing: true,
        });
        return;
      }

      // 没有替换图片，只更新标题和描述
      callOnSave({
        assetId: editingAssetId,
        title,
        description,
        isEditing: true,
      });
      return;
    }

    let finalThumbnailBlob = null;
    const isRole = category === 'role';
    const aspectWidth = 200;
    const aspectHeight = isRole ? Math.round(200 * (142 / 86)) : 200; // 角色保持 86:142 比例，其他正方形

    // 包装 onSave 调用：await 完成后恢复按钮状态（仅在失败时需要，成功时 modal 会关闭）
    const callOnSave = async (payload) => {
      try {
        await onSave(payload);
      } finally {
        setSaving(false);
      }
    };

    if (nodeData.type === 'image' && cropperRef.current) {
        // 获取裁剪后的 Blob
        const canvas = cropperRef.current.cropper.getCroppedCanvas({
            width: aspectWidth, height: aspectHeight,
            imageSmoothingQuality: 'high'
        });
        if (canvas) {
            canvas.toBlob((blob) => {
                callOnSave({
                  title,
                  description,
                  category,
                  type: nodeData.type,
                  thumbnailBlob: blob,
                  bgColor: nodeData.bgColor, // 【新增】保存背景色
                  // 🛡️ 分类是 global scope 时强制全局，不依赖用户是否勾选"部署全局"复选框
                  // （复选框只在 text 类型显示，image/video/audio 选中全局分类时没有勾选入口，
                  // 之前会导致分类标签全局、但资产归属字段没跟着置真的孤儿资产）
                  isGlobal: !!isGlobalSpell || isCategoryGlobalScope,
                  isEncrypted: !!(isGlobalSpell && isEncryptedSpell),
                });
            }, 'image/jpeg', 0.85);
            return;
        }
    } else if (nodeData.type === 'video') {
         finalThumbnailBlob = videoThumbBlob;
    } else if (nodeData.type === 'workflow' || nodeData.type === 'text') {
         // 🆕 工作流/咒语：从裁剪器获取当前裁剪结果（single 整图裁剪，或 split 左右拼接）
         const result = await getWorkflowCoverBlobFromCropper();
         if (result && result.__error) {
           alert(result.__error);
           setSaving(false);
           return;
         }
         finalThumbnailBlob = result;
    }

    callOnSave({
      title,
      description,
      category,
      type: nodeData.type,
      thumbnailBlob: finalThumbnailBlob,
      bgColor: nodeData.bgColor, // 【新增】保存背景色
      // 🛡️ 分类是 global scope 时强制全局，理由同上（image 分支的注释）
      isGlobal: !!isGlobalSpell || isCategoryGlobalScope,
      isEncrypted: !!(isGlobalSpell && isEncryptedSpell),
    });
  };

  const currentTypeCats = CATEGORIES[nodeData.type] || [];
  const isImageByCheck = nodeData.type === 'image';
  const isVideoByCheck = nodeData.type === 'video';
  const isAudioByCheck = nodeData.type === 'audio';
  const isRoleCategory = category === 'role';
  const isTextType = nodeData.type === 'text';
  const isWorkflowType = nodeData.type === 'workflow';
  const isEditingCover = nodeData?.isEditingCover; // 🆕 编辑封面模式

  // ── 分类下拉选项 ──
  // workflow 类型：动态加载的 workflowCategories
  // image/video/audio 类型：动态加载的对应分类
  // 统一使用动态加载的分类
  const categoryOptions = (() => {
    // workflow 类型：使用动态加载的分类
    if (isWorkflowType) {
      return workflowCategories.length > 0
        ? workflowCategories.map(c => ({ value: c.id, label: c.label, locked: true }))
        : [{ value: 'default', label: '默认分类', locked: true }];
    }
    // image 类型：使用动态加载的分类
    if (isImageByCheck) {
      return imageCategories.length > 0
        ? imageCategories.map(c => ({ value: c.id, label: c.label, locked: true }))
        : [{ value: 'misc', label: '杂项 (Misc)', locked: true }];
    }
    // video 类型：使用动态加载的分类
    if (isVideoByCheck) {
      return videoCategories.length > 0
        ? videoCategories.map(c => ({ value: c.id, label: c.label, locked: true }))
        : [{ value: 'misc', label: '杂项 (Misc)', locked: true }];
    }
    // audio 类型：使用动态加载的分类
    if (isAudioByCheck) {
      return audioCategories.length > 0
        ? audioCategories.map(c => ({ value: c.id, label: c.label, locked: true }))
        : [{ value: 'misc', label: '杂项 (Misc)', locked: true }];
    }
    // text 类型：使用动态加载的分类
    return textCategories.length > 0
      ? textCategories.map(c => ({ value: c.id, label: c.label, locked: true }))
      : [{ value: 'misc', label: '杂项 (Misc)', locked: true }];
  })();

  // 🛡️ 当前选中分类的 scope（从对应类型的分类列表里按 category_key 反查）
  // 用来判断保存时是否要强制 is_global=true —— 否则「分类是 global scope，
  // 但资产的 is_global 字段没跟着置真」会产生孤儿资产：资料库任何视图都查不到，
  // 删分类时的计数又能扫到，删不掉（复盘见 2026-09 环艺学姐"示例2"分类事故）
  const currentCategoryList = isWorkflowType ? workflowCategories
    : isImageByCheck ? imageCategories
    : isVideoByCheck ? videoCategories
    : isAudioByCheck ? audioCategories
    : textCategories;
  const isCategoryGlobalScope = currentCategoryList.find(c => c.id === category)?.scope === 'global';

  // 🗑️ 已移除旧的分类管理函数（handleAddCategory, handleRenameCategory, handleDeleteCategory, handleMoveCategory）
  // 现在统一使用 CategoryManagement 后台组件管理分类

  // 标题输入框（工作流/非工作流复用同一段 JSX，只是外层容器版式不同）
  const titleFieldNode = (
    <div>
        <label style={modalStyle.label}>标题 (TITLE)</label>
        <input
            style={{...modalStyle.input, opacity: isEditingCover ? 0.6 : 1, cursor: isEditingCover ? 'not-allowed' : 'text'}}
            value={title}
            onChange={(e) => !isEditingCover && setTitle(e.target.value)}
            autoFocus={!isEditingCover}
            placeholder="输入名称..."
            disabled={isEditingCover}
        />
    </div>
  );

  // 所属分类下拉（同上，复用）
  // - 普通用户保存 text 时：分类对他们无意义（无"部署全局"权限），直接隐藏，默认 category='prompt' 落到自己后台的咒语库
  // - 其他类型 / super_admin：正常显示
  const categoryFieldNode = !(isTextType && !isSuperAdmin) && (
    <div style={{opacity: isEditingCover ? 0.6 : 1, pointerEvents: isEditingCover ? 'none' : 'auto'}}>
        <label style={modalStyle.label}>所属分类 (CATEGORY)</label>
        <CustomSelect
            options={categoryOptions}
            value={category}
            onChange={setCategory}
            // 所有类型统一使用 CategoryManagement 后台管理，不再提供内嵌管理功能
            manageable={false}
            disabled={isEditingCover}
        />
    </div>
  );

  // 🆕 描述词（图片/视频/音频）：连到下游生成节点时整段作为提示词传给模型
  const descriptionFieldNode = (isImageByCheck || isVideoByCheck || isAudioByCheck) && (
    <div style={{opacity: isEditingCover ? 0.6 : 1, pointerEvents: isEditingCover ? 'none' : 'auto'}}>
        <label style={modalStyle.label}>描述 (DESCRIPTION)</label>
        {/* resize:'none' —— 右下角原生拉伸手柄没必要（要大就点全屏），
            留着还会和全屏按钮抢同一个角落。 */}
        <div style={{ position: 'relative' }}>
          <textarea
              style={{
                ...modalStyle.input,
                height: '160px', resize: 'none',
                padding: '10px 10px 40px',
                lineHeight: 1.5, fontFamily: 'inherit',
              }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="描述这张图的内容，会作为提示词传给模型…"
              disabled={isEditingCover}
          />
          {/* 全屏编辑：进去有文本整理 + 按住说话 */}
          <div
            onClick={() => { if (!isEditingCover) setShowDescFullscreen(true); }}
            title="全屏编辑"
            style={{
              position: 'absolute', right: 8, bottom: 8,
              width: 28, height: 28, borderRadius: 6,
              background: 'var(--bg-hover)',
              border: '1px solid var(--border-strong)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.2s', backdropFilter: 'blur(4px)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--text-hover)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border-strong)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <Maximize2 size={14} color="var(--text-secondary)" strokeWidth={2} />
          </div>
        </div>
        <FullscreenTextEditor
          isOpen={showDescFullscreen}
          onClose={() => setShowDescFullscreen(false)}
          value={description}
          onChange={setDescription}
          title="编辑描述词"
        />
    </div>
  );

  // 🌍/🔒 全局咒语/工作流选项 + 加密选项 + 非管理员提示（同上，复用）
  const globalOptionsNode = (
    <>
      {!isEditingCover && isSuperAdmin && (nodeData.type === 'text' || nodeData.type === 'workflow') && (
        <>
          <div
            onClick={() => {
              const next = !isGlobalSpell;
              setIsGlobalSpell(next);
              if (!next) setIsEncryptedSpell(false);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              cursor: 'pointer', padding: '8px 12px', marginTop: '8px',
              borderRadius: '8px', fontSize: '13px',
              background: isGlobalSpell ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
              border: isGlobalSpell ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(255,255,255,0.1)',
              color: isGlobalSpell ? '#3b82f6' : '#888',
              transition: 'all 0.2s', userSelect: 'none'
            }}
          >
            <div style={{
              width: '14px', height: '14px', borderRadius: '3px',
              background: isGlobalSpell ? '#3b82f6' : 'transparent',
              border: isGlobalSpell ? '2px solid #3b82f6' : '2px solid #555',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s', flexShrink: 0
            }}>
              {isGlobalSpell && <div style={{ width: '6px', height: '6px', borderRadius: '1px', background: '#fff' }} />}
            </div>
            {nodeData.type === 'workflow' ? '部署为全局工作流' : '部署为全局咒语'}
          </div>

          {/* 🔒 加密选项：仅咒语类型显示，工作流不显示 */}
          {nodeData.type === 'text' && (
            <div
              onClick={() => { if (isGlobalSpell) setIsEncryptedSpell(!isEncryptedSpell); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                cursor: isGlobalSpell ? 'pointer' : 'not-allowed',
                padding: '8px 12px', marginTop: '8px',
                borderRadius: '8px', fontSize: '13px',
                background: isEncryptedSpell ? 'rgba(234, 179, 8, 0.12)' : 'transparent',
                border: isEncryptedSpell ? '1px solid rgba(234, 179, 8, 0.4)' : '1px solid rgba(255,255,255,0.1)',
                color: isGlobalSpell ? (isEncryptedSpell ? '#fbbf24' : '#aaa') : '#666',
                opacity: isGlobalSpell ? 1 : 0.6,
                transition: 'all 0.2s', userSelect: 'none'
              }}
            >
              <div style={{
                width: '14px', height: '14px', borderRadius: '3px',
                background: isEncryptedSpell ? '#f59e0b' : 'transparent',
                border: isEncryptedSpell ? '2px solid #f59e0b' : '2px solid #555',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0
              }}>
                {isEncryptedSpell && <div style={{ width: '6px', height: '6px', borderRadius: '1px', background: '#fff' }} />}
              </div>
              加密咒语
            </div>
          )}
        </>
      )}

      {/* 🔒 非管理员保存加密咒语时的提示 */}
      {!isSuperAdmin && nodeData.isSystemSpell && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 12px', marginTop: '8px',
          borderRadius: '8px', fontSize: '13px',
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          color: '#fbbf24',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          保存后仍为加密锁定状态
        </div>
      )}
    </>
  );

  const hintTextNode = (
    <div style={{fontSize:'12px', color:'var(--text-muted)', lineHeight:'1.5'}}>
       提示：
       {isImageByCheck ? "左侧裁剪的内容将作为后台列表中的缩略图（Thumbnail），拖入图板时仍使用原图。" :
        isVideoByCheck ? "已自动截取视频中间画面作为封面缩略图。" :
        (isWorkflowType && coverMode === 'split') ? "左右两侧各选一张图裁剪，保存时自动无缝拼接成一张长图封面。" :
        (isWorkflowType || isTextType) ? "点击上方按钮从当前图板选择图片作为封面。" :
        "该类型资产暂不支持自定义封面。"}
    </div>
  );

  return (
    <div style={modalStyle.overlay}>
      <style>{`
        .cropper-point.point-e, .cropper-point.point-n, .cropper-point.point-w, .cropper-point.point-s { display: none !important; }
        .cropper-line { display: none !important; }
        .cropper-view-box { outline: 1px solid #fff; outline-color: rgba(255, 255, 255, 0.75); }
      `}</style>
      <div style={isWorkflowType ? workflowModalStyle.container : modalStyle.container}>
        {/* Header */}
        <div style={modalStyle.header}>
          <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
             {isImageByCheck ? <ImageIcon size={20} color="#3b82f6" /> : isVideoByCheck ? <Film size={20} color="#22c55e" /> : <Layers size={20} color="#f97316" />}
             <span style={{fontWeight:'bold', color:'var(--text-primary)', letterSpacing:'1px'}}>
               {isEditingCover ? '编辑封面' : (isEditingAsset ? '编辑资产' : '保存到资料库')}
             </span>
          </div>
          <div onClick={onClose} style={{cursor:'pointer', color:'var(--text-secondary)'}}><X size={20} /></div>
        </div>

        <div style={isWorkflowType ? workflowModalStyle.body : modalStyle.body}>
            {/* 裁剪区：工作流用上半区大图（workflowModalStyle.topArea），其他类型用左栏（modalStyle.leftCol） */}
            <div style={isWorkflowType ? workflowModalStyle.topArea : modalStyle.leftCol}>
                {/* 🆕 隐藏的文件选择器（编辑资产时替换图片） */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                {/* 🆕 工作流封面模式切换：单图裁剪 / 左右拼接（编辑封面时也允许切换，不影响标题分类等锁定字段） */}
                {isWorkflowType && (
                    <div style={{
                        position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)',
                        display: 'flex', gap: '4px', background: 'rgba(0,0,0,0.6)',
                        border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', padding: '2px', zIndex: 5
                    }}>
                        {[{ id: 'single', label: '单图裁剪' }, { id: 'split', label: '左右拼接' }].map(m => (
                            <button
                                key={m.id}
                                onClick={() => {
                                    if (coverMode === m.id) return;
                                    setCoverMode(m.id);
                                    // 切换模式时清空对方模式已存的裁剪状态，避免保存时数据歧义
                                    if (m.id === 'single') {
                                        setSplitLeftUrl(null); setSplitRightUrl(null);
                                        setSplitLeftBlob(null); setSplitRightBlob(null);
                                    } else {
                                        setWorkflowCoverCropUrl(null);
                                        setWorkflowCoverBlob(null);
                                    }
                                }}
                                style={{
                                    padding: '4px 10px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                                    fontSize: '11px', fontWeight: coverMode === m.id ? 'bold' : '400',
                                    background: coverMode === m.id ? '#3b82f6' : 'transparent',
                                    color: coverMode === m.id ? '#fff' : '#aaa',
                                    transition: 'all 0.15s'
                                }}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                )}
                {isImageByCheck ? (
                    <Cropper
                        src={currentImageSrc || nodeData.localCacheUrl || nodeData.output || nodeData.url}
                        style={{ height: '100%', width: '100%' }}
                        aspectRatio={isRoleCategory ? 86 / 142 : 1} // 角色用长方形，其他正方形
                        guides={false}
                        center={false}
                        viewMode={1}
                        dragMode="move"
                        ref={cropperRef}
                        background={false}
                        autoCropArea={0.8}
                        toggleDragModeOnDblclick={false}
                    />
                ) : isVideoByCheck ? (
                    videoThumbUrl ?
                    <img src={videoThumbUrl} style={{width:'100%', height:'100%', objectFit:'contain'}} alt="Video Thumbnail" /> :
                    <div style={{color:'#666', fontSize:'12px'}}>正在生成封面...</div>
                ) : isWorkflowType && coverMode === 'split' ? (
                    // 🎨 工作流封面 · 左右拼接模式：两个槛位并排，各自独立挂载 <Cropper>，两边同时可操作，
                    // 互不影响、互不顶掉（之前共用一个 ref 切换会导致对方的裁剪器卸载）
                    <div style={{display:'flex', width:'100%', height:'100%'}}>
                        {['left', 'right'].map((side, idx) => {
                            const sideUrl = side === 'left' ? splitLeftUrl : splitRightUrl;
                            const sideRef = side === 'left' ? splitLeftCropperRef : splitRightCropperRef;
                            return (
                                <div
                                    key={side}
                                    style={{
                                        flex: 1, position: 'relative', height: '100%',
                                        borderLeft: idx === 1 ? '1px dashed rgba(255,255,255,0.35)' : 'none',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                                    }}
                                >
                                    {sideUrl ? (
                                        // 已选好源图：始终显示裁剪框，可随时调整，直到点击保存
                                        // 🛡️ cropperjs 内部有 minContainerWidth/Height 硬下限（200×100），
                                        // 而这里每一半实际渲染宽度只有 leftCol(300px)/2 ≈ 150px，低于这个下限。
                                        // cropperjs 会把内部坐标系强行按 200px 宽计算，但外层 div 仍按 150px 渲染并
                                        // overflow:hidden 裁切，于是裁剪框在右侧被允许越出真实可见范围（左/上/下因为
                                        // 尺寸够或被居中对齐而看不出问题，只有右侧露出这个坐标错位）。
                                        // 显式把 min 下限降到真实渲染尺寸，让 cropperjs 的内部坐标系与实际可见区域对齐。
                                        <Cropper
                                            src={sideUrl}
                                            style={{ height: '100%', width: '100%' }}
                                            aspectRatio={(WORKFLOW_CARD_WIDTH / 2) / WORKFLOW_CARD_HEIGHT}
                                            guides={true}
                                            center={true}
                                            viewMode={1}
                                            dragMode="move"
                                            ref={sideRef}
                                            background={false}
                                            autoCropArea={0.8}
                                            toggleDragModeOnDblclick={false}
                                            minContainerWidth={1}
                                            minContainerHeight={1}
                                        />
                                    ) : (
                                        <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'8px', color:'#666'}}>
                                            <Sparkles size={28} strokeWidth={1} />
                                            <span style={{fontSize:'10px'}}>{side === 'left' ? '左半图未设置' : '右半图未设置'}</span>
                                        </div>
                                    )}

                                    <button
                                        onClick={() => handleSetWorkflowCover(side)}
                                        style={{
                                            position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
                                            padding: '4px 10px', borderRadius: '4px',
                                            background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)',
                                            color: '#fff', fontSize: '10px', cursor: 'pointer', fontWeight: '400',
                                            whiteSpace: 'nowrap', transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.75)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                                    >
                                        {sideUrl ? '重新选图' : (side === 'left' ? '裁剪左图' : '裁剪右图')}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ) : isWorkflowType || isTextType ? (
                    // 🎨 工作流（单图模式）/ 咒语封面：两种状态
                    <>
                        {workflowCoverCropUrl ? (
                            // 状态 1: 裁剪模式 - 显示裁剪器
                            <Cropper
                                src={workflowCoverCropUrl}
                                style={{ height: '100%', width: '100%' }}
                                aspectRatio={isWorkflowType ? WORKFLOW_CARD_WIDTH / WORKFLOW_CARD_HEIGHT : 1} // 🔲 工作流长图裁剪，咒语仍正方形
                                guides={true}
                                center={true}
                                viewMode={1}
                                dragMode="move"
                                ref={workflowCropperRef}
                                background={false}
                                autoCropArea={0.8}
                                toggleDragModeOnDblclick={false}
                            />
                        ) : (
                            // 状态 2: 未设置/无裁剪 - 显示占位图
                            <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', color:'#666'}}>
                                <Sparkles size={48} strokeWidth={1} />
                            </div>
                        )}

                        {/* 底部按钮：始终显示「选择封面图片」 */}
                        <button
                            onClick={() => handleSetWorkflowCover(null)}
                            style={{
                                position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
                                padding: '4px 12px', borderRadius: '4px',
                                background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)',
                                color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: '400',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.75)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                        >
                            选择封面图片
                        </button>
                    </>
                ) : (
                    <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', color:'#666'}}>
                        <Sparkles size={48} strokeWidth={1} />
                        <span style={{fontSize:'12px'}}>无预览可用</span>
                    </div>
                )}

                {/* 🆕 替换图片按钮 - 编辑资产模式下显示 */}
                {isEditingAsset && (isImageByCheck || isVideoByCheck) && (
                  <button
                    onClick={handleReplaceImage}
                    style={{
                      position: 'absolute', bottom: '8px', left: '8px',
                      padding: '6px 12px', borderRadius: '6px',
                      background: 'rgba(59, 130, 246, 0.9)', border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: '500',
                      display: 'flex', alignItems: 'center', gap: '4px',
                      transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 1)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.9)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <Plus size={12} />
                    替换图片
                  </button>
                )}

                {/* 浮动提示 */}
                {isImageByCheck && !isEditingAsset && (
                    <div style={{position:'absolute', bottom:'10px', left:'0', right:'0', textAlign:'center', pointerEvents:'none'}}>
                        <span style={{background:'rgba(0,0,0,0.6)', color:'#fff', padding:'4px 8px', borderRadius:'12px', fontSize:'10px'}}>
                            裁剪缩略图
                        </span>
                    </div>
                )}
            </div>

            {isWorkflowType ? (
                /* 工作流专属：表单区挪到裁剪大图下方，标题和分类并排，把裁剪区留给上面的大图 */
                <div style={workflowModalStyle.bottomArea}>
                    <div style={{flex: 1}}>{titleFieldNode}</div>
                    <div style={{flex: 1}}>{categoryFieldNode}</div>
                    <div style={{flexShrink: 0, display:'flex', flexDirection:'column', gap:'6px'}}>
                        {globalOptionsNode}
                    </div>
                    <div style={{flex: '1 1 200px', minWidth:0}}>{hintTextNode}</div>
                </div>
            ) : (
                /* Right: Inputs（image/video/audio/text 保持原有左右分栏版式） */
                <div style={modalStyle.rightCol}>
                    {titleFieldNode}
                    {categoryFieldNode}
                    {globalOptionsNode}
                    {descriptionFieldNode}
                    <div style={{marginTop:'auto'}}>{hintTextNode}</div>
                </div>
            )}
        </div>

        {/* Footer */}
        <div style={modalStyle.footer}>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid var(--border-strong)',
            color: 'var(--text-body)', padding: '8px 20px', borderRadius: '6px', cursor:'pointer'
          }}>
            取消
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            background: saving ? '#555' : '#3b82f6', border: 'none',
            color: '#fff', padding: '8px 24px', borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer', fontWeight:'bold', display:'flex', alignItems:'center', gap:'8px',
            boxShadow: saving ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.4)',
            opacity: saving ? 0.7 : 1, transition: 'all 0.2s'
          }}>
            {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
            {saving ? '保存中...' : (isEditingCover ? '确认更新' : (isEditingAsset ? '更新资产' : '确认入库'))}
          </button>
        </div>
      </div>

      {/* 🆕 工作流封面图片选择器 */}
      {showWorkflowCoverPicker && (
        <ImagePickerModal
          images={workflowImages}
          onSelect={handleSelectWorkflowCoverImage}
          onClose={() => setShowWorkflowCoverPicker(false)}
        />
      )}
    </div>
  );
}